import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import {
  INSTAGRAM_DEFAULT_POST_TYPE,
  INSTAGRAM_POST_TYPES,
  InstagramDestinationPlatformData,
  InstagramPublicationSettings,
  normalizeInstagramPublicationSettings,
  resolveInstagramPublicationSettings,
} from "app/js/src/domain/destinations";
import { Subscription } from "rxjs";

type InstagramPublishForm = {
  defaultPostType: FormControl<string>;
  shareToFeed: FormControl<boolean>;
  isAiGenerated: FormControl<boolean>;
};

@Component({
  selector: "evrideo-clipping-studio-instagram-publish-settings",
  templateUrl: "./clipping-studio-instagram-publish-settings.component.html",
  styleUrls: ["./clipping-studio-instagram-publish-settings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClippingStudioInstagramPublishSettingsComponent
  implements OnChanges, OnDestroy
{
  @Input({ required: true }) destinationUid!: string;
  @Input({ required: true }) destinationName!: string;
  @Input() destinationDefaults: InstagramDestinationPlatformData | null = null;
  @Input() publicationSettings: InstagramPublicationSettings | null = null;
  @Input() readonly = false;

  @Output() platformDataChange = new EventEmitter<InstagramPublicationSettings>();

  readonly instagramPostTypes = INSTAGRAM_POST_TYPES;
  readonly shareToFeedTooltip =
    "Applies to Reels only. Not used for Story.";

  readonly form = new FormGroup<InstagramPublishForm>({
    defaultPostType: new FormControl<string>(INSTAGRAM_DEFAULT_POST_TYPE, {
      nonNullable: true,
    }),
    shareToFeed: new FormControl<boolean>(true, { nonNullable: true }),
    isAiGenerated: new FormControl<boolean>(false, { nonNullable: true }),
  });

  private readonly subscription = new Subscription();
  private syncing = false;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {
    this.subscription.add(
      this.form.valueChanges.subscribe(() => this.onFormChanged()),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["destinationDefaults"] ||
      changes["publicationSettings"] ||
      changes["destinationUid"] ||
      changes["destinationName"] ||
      changes["readonly"]
    ) {
      this.syncFromInputs();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onPostTypeChange(value: string): void {
    if (this.readonly) {
      return;
    }
    this.form.controls.defaultPostType.setValue(value);
  }

  private syncFromInputs(): void {
    this.syncing = true;
    const resolved = resolveInstagramPublicationSettings(
      this.publicationSettings,
      this.destinationDefaults,
    );

    this.form.reset(
      {
        defaultPostType:
          resolved.default_post_type ?? INSTAGRAM_DEFAULT_POST_TYPE,
        shareToFeed: resolved.share_to_feed ?? true,
        isAiGenerated: resolved.is_ai_generated ?? false,
      },
      { emitEvent: false },
    );

    if (this.readonly) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }

    this.syncing = false;
    this.changeDetectorRef.markForCheck();
  }

  private onFormChanged(): void {
    if (this.syncing) {
      return;
    }
    this.platformDataChange.emit(this.toPublicationSettings());
    this.changeDetectorRef.markForCheck();
  }

  private toPublicationSettings(): InstagramPublicationSettings {
    const raw = this.form.getRawValue();
    return normalizeInstagramPublicationSettings({
      default_post_type: raw.defaultPostType,
      share_to_feed: raw.shareToFeed,
      is_ai_generated: raw.isAiGenerated,
    });
  }
}
